// src/data/shelterBankDetails.ts
// --------------------------------
// Repository for `shelter_bank_details` (db-127) — Bank Name + Bank Account for a
// DFV Service. Held in a SEPARATE admin-only table (RLS get_user_role='admin')
// rather than on `shelters`, because shelter/caseworker logins can read their own
// `shelters` row and RLS is row-level (can't hide a single column) — a separate
// table guarantees the bank number is never returned to a non-admin. 1:1 with a
// shelter (UNIQUE shelter_id); edited via upsert. Field names mirror the Unleashed
// Customer CSV headers ("Bank Name" / "Bank Account").
import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ShelterBankDetailsRow = Database["public"]["Tables"]["shelter_bank_details"]["Row"];

const COLS: string = "id, shelter_id, bank_name, bank_account, updated_by, updated_at";

// Fetch the bank details for a shelter (admin-only via RLS). Null if none / error.
export async function getBankDetails(
  shelterId: string | undefined
): Promise<ShelterBankDetailsRow | null> {
  const c = getTypedClient();
  if (!c || !shelterId) return null;
  const { data, error } = await c
    .from("shelter_bank_details")
    .select(COLS)
    .eq("shelter_id", shelterId)
    .maybeSingle();
  if (error) {
    logger.warn("getBankDetails failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterBankDetailsRow) ?? null;
}

// All bank-detail rows (admin-only via RLS) — used by the Customer sync so the diff can
// compare Bank Name / Account (which live off `shelters`). Returns [] on error.
export async function listAllBankDetails(): Promise<ShelterBankDetailsRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  const { data, error } = await c.from("shelter_bank_details").select(COLS);
  if (error) {
    logger.warn("listAllBankDetails failed:", error.message);
    return [];
  }
  return (data as unknown as ShelterBankDetailsRow[]) ?? [];
}

export type BankSaveResult = { error: { message: string } | null };

// Upsert the bank details for a shelter — insert on first save, update thereafter
// (keyed on the UNIQUE shelter_id). Empty strings are stored as NULL. The db-127
// trigger logs the change to shelter_change_log with the acting user.
export async function upsertBankDetails(
  shelterId: string,
  fields: { bank_name: string; bank_account: string },
  updatedBy: string
): Promise<BankSaveResult> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No Supabase client" } };
  const { error } = await c.from("shelter_bank_details").upsert(
    {
      shelter_id: shelterId,
      bank_name: fields.bank_name?.trim() || null,
      bank_account: fields.bank_account?.trim() || null,
      updated_by: updatedBy || null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "shelter_id" }
  );
  if (error) logger.warn("upsertBankDetails failed:", error.message);
  return { error: error ? { message: error.message } : null };
}
